import { Clone, kStructuredCloneMarker, kMoveMarker, kMoveState, kMoveMethod, Send, kCloneMethod, NAMESPACE, Move, isStructuredClone, kSendMarker } from './transfer.js';
import { ConfigurationError } from '../../../errors/shared/base.js';
import { iteratorReduce } from '../../../iterator/shared/iterator.js';
import { asyncIterForEach, iteratorToAsync } from '../../../iterator/shared/async.js';
import { Result, Ok, Err } from '../../../result/shared/result.js';
import { SendError, StructuredCloneError, MoveError } from './errors.js';
import { restore } from './restoreRegistry.js';

const DERIVE_PREFIX = NAMESPACE + '.derive';
/**
 * Утилита выполняет структурное клонирование объекта {@linkcode IStructuredClone} и возвращает готовую
 * к перемещению структуру {@linkcode Clone}. Если объект является перемещаемым {@linkcode IMove}, то
 * перед структурным клонированием вызывается метод перемещения объекта
 *
 * @export
 * @template T
 * @param {(IStructuredClone<T> | ISend<T> | ICloneable<T> | IMove<T>)} val - объект, который можно клонировать
 * @return {Promise<Clone<T>>} - возвращает промис, потому что метод перемещения объекта типа
 * `IMove` может быть асинхронным
 *
 * @throws {StructuredCloneError} - если метод {@linkcode IStructuredClone.toStructuredClone} выбросил
 * исключеение
 * @throws {SendError} - если не удалось создать копию объекта {@linkcode ISend}
 * @throws {MoveError} - если метод вызывается на уже перемещенном объекте типа {@linkcode IMove}
 * @throws {MoveError} - если вызов метода перемещения объекта типа {@linkcode IMove} завершилось с
 * ошибкой
 */
async function transfer(val) {
    let cloneStruct;
    let _val = val;
    if (Send.isCloneable(val)) {
        try {
            const result = val[kCloneMethod]();
            // Значит Promise
            if (!Send.is(result)) {
                _val = await result;
            }
            else {
                _val = result;
            }
        }
        catch (error) {
            throw new SendError({
                message: `Создание копии объекта ${_val?.constructor?.name || _val} завершилось с ошибкой`,
                domain: NAMESPACE + '.transfer',
                details: error,
            });
        }
    }
    else if (Move.is(_val)) {
        const result = prepareMove(_val);
        if (result) {
            await result;
        }
    }
    try {
        cloneStruct = _val.toStructuredClone();
    }
    catch (error) {
        if (StructuredCloneError.is(error)) {
            throw error;
        }
        throw new StructuredCloneError({
            message: `Структурное клонирование объекта ${_val?.constructor?.name || _val} завершилось с ошибкой`,
            domain: NAMESPACE + '.transfer',
            details: error,
        });
    }
    return cloneStruct;
}
function prepareMove(target) {
    if (target[kMoveState]) {
        throw new MoveError({
            message: `Повторая попытка перемещения объекта ${target?.constructor?.name}, он уже был перемещен`,
            domain: NAMESPACE + '.move',
        });
    }
    try {
        const result = target[kMoveMethod]();
        if (result) {
            return result
                .then(() => {
                target[kMoveState] = true;
            })
                .catch(error => {
                throw new MoveError({
                    message: `Перемещение объекта ${target?.constructor?.name} завершилось с ошибкой`,
                    domain: NAMESPACE + '.move',
                    details: error,
                });
            });
        }
        if (!target[kMoveState]) {
            target[kMoveState] = true;
        }
        return undefined;
    }
    catch (error) {
        throw new MoveError({
            message: `Перемещение объекта ${target?.constructor?.name} завершилось с ошибкой`,
            domain: NAMESPACE + '.move',
            details: error,
        });
    }
}
const TRANSFERABLE_ARRAY_MARKER = 'core.threads.TransferableArray';
const TransferableArray = {
    toTransferable: toTransferableArray,
    restore: {
        [kStructuredCloneMarker]: TRANSFERABLE_ARRAY_MARKER,
        from(serialized) {
            return toTransferableArray(Result.collect(serialized.map(s => (Clone.isStruct(s) ? restore(s) : Ok(s)))).unwrap());
        },
    },
};
function toTransferableArray(arr) {
    arr[kStructuredCloneMarker] = TRANSFERABLE_ARRAY_MARKER;
    arr[kMoveMarker] = 1;
    arr[kMoveState] = false;
    arr[kMoveMethod] = () => asyncIterForEach(iteratorToAsync(arr), async (i) => {
        if (Move.is(i)) {
            await transfer(i);
        }
    });
    arr.toStructuredClone = () => {
        const { value, transferable } = iteratorReduce(arr, (acc, i) => {
            if (isStructuredClone(i)) {
                const [marker, val, transferable] = i.toStructuredClone();
                acc.value.push([marker, val]);
                if (Array.isArray(transferable) && transferable.length) {
                    acc.transferable.push(...transferable);
                }
            }
            else {
                acc.value.push(i);
            }
            return acc;
        }, {
            transferable: [],
            value: [],
        }).expect('Неизвестная ошибка при структурном клонировании TransferableArray', TRANSFERABLE_ARRAY_MARKER);
        return [TRANSFERABLE_ARRAY_MARKER, value, transferable];
    };
    return arr;
}
/**
 * Трансформирует функцию в разделяемую {@link ICopyable}, добавляя к ней методы для структурного клонирования
 *
 * ```ts
 * const foo = () => {
 *  return 1;
 * };
 *
 * const fooSend = deriveSendFn(foo);
 *
 * sender.send(fooSend);
 * ```
 *
 * Возвращает ошибку, если функция уже является разделяемой {@link ICopyable}
 *
 * **UNSAFE:** Разделяемая функция не должна иметь ссылок на переменные вне своего скоупа
 *
 * @export
 * @template F
 * @param {F} fn
 * @param {string} [name] - маркер, который будет использован для структурного клонирования и восстановления
 * @return {(Result<F & ICopyable<EmptyData>, ConfigurationError>)}
 */
// oxlint-disable-next-line typescript/no-unsafe-function-type
function deriveSendFn(fn, name) {
    const derivedName = name || `${DERIVE_PREFIX}.${fn.name}`;
    const sendable = fn;
    if (Send.is(sendable)) {
        return Err(new ConfigurationError({
            message: `Функция ${derivedName} уже является разделяемой`,
            domain: NAMESPACE + '.deriveSend',
        }));
    }
    sendable[kStructuredCloneMarker] = derivedName;
    sendable[kSendMarker] = 1;
    sendable.toStructuredClone = () => [derivedName, undefined];
    return Ok(sendable);
}
/**
 * Создает функцию восстановления {@link IRestore} из разделяемой {@link ICopyable}
 *
 * ```ts
 * const foo = () => { return 1; };
 *
 * const fooSend = deriveSendFn(foo);
 *
 * const fooRestore = deriveRestoreFn(fooSend);
 *
 * register(fooRestore);
 *
 * assert.equal(restore(await transfer(fooSend)).unwrap(), foo);
 * ```
 *
 * @export
 * @template S
 * @param {S} sendable
 * @return {IRestore<InferStructuredCloneValueType<S>, S>}
 */
// oxlint-disable-next-line typescript/no-unsafe-function-type
function deriveRestoreFn(sendable) {
    return {
        [kStructuredCloneMarker]: sendable[kStructuredCloneMarker],
        from() {
            return sendable;
        },
    };
}

export { TransferableArray, deriveRestoreFn, deriveSendFn, prepareMove, transfer };
//# sourceMappingURL=utils.js.map
